import { _ } from "../../core/polyglot/polyglot.js";
import { timesIcon } from "../../core/elements/icons/index.js";
import { widget } from "@foretagsplatsen/widgetjs";
import FileSelectionButtonWidget from "../../core/widgets/FileSelectionButtonWidget.js";
import OldSpinnerWidget from "../../deprecated/OldSpinnerWidget/OldSpinnerWidget.js"; // eslint-disable-line import-x/no-deprecated -- todo: migrate to new spinner
import updatingTitleWidget from "./updatingTitleWidget.js";

/**
 * `chartsOfAccountsSectionWidget` is a section appearing in the
 * company settings page to manage user available chart of accounts.
 *
 * @param {string} spec.currentLanguage - Default language used when creating a new chart of accounts (disable feature)
 * @param {company} spec.company - Current company being edited
 * @param {boolean} spec.chartOfAccountsEditorEnabled - If true, display links to edit
 * the chart of accounts.
 * @param {boolean} spec.accountMappingEnabled - If true, display links to edit
 * the account mapping of chart of accounts that have account mapping turned on.
 * @param {function(ChartOfAccount):boolean} spec.canDeleteChartOfAccounts - Returns true if the chartOfAccount can be deleted
 * @param {function(ChartOfAccount):boolean} spec.isAgencyChartOfAccounts - Returns true if the chartOfAccount is owned by the agency
 */
export default widget.subclass((that, my) => {
	my.initialize = function (spec) {
		my.super(spec);

		my.chartsOfAccounts = [];
		my.currentLanguage = spec.currentLanguage;
		my.company = spec.company;
		my.chartOfAccountsEditorEnabled = spec.chartOfAccountsEditorEnabled;
		my.accountMappingEnabled = spec.accountMappingEnabled;
		my.canDeleteChartOfAccounts = spec.canDeleteChartOfAccounts;
		my.isAgencyChartOfAccounts = spec.isAgencyChartOfAccounts;
	};

	that.onChartOfAccountsRemoved = my.events.createEvent();
	that.onChartOfAccountsImported = my.events.createEvent();
	that.onChartOfAccountsClicked = my.events.createEvent();

	my.listOfChartsOfAccountsTitle = updatingTitleWidget({
		title: () => {},
	});

	// eslint-disable-next-line import-x/no-deprecated -- todo: migrate to new spinner
	my.spinnerWidget = new OldSpinnerWidget({
		size: 64,
	});

	// eslint-disable-next-line import-x/no-deprecated -- todo: migrate to new spinner
	my.uploadSpinner = new OldSpinnerWidget({
		size: 32,
		visible: false,
	});

	that.pend = my.listOfChartsOfAccountsTitle.pend;
	that.succeed = my.listOfChartsOfAccountsTitle.succeed;

	that.setChartsOfAccounts = function (chartsOfAccounts) {
		my.chartsOfAccounts = chartsOfAccounts.sort((a, b) => a.compare(b));
		that.update();
	};

	that.renderContentOn = function (html) {
		if (!my.chartsOfAccounts) {
			my.renderLoadingOn(html);
			return;
		}
		if (my.chartsOfAccounts.length === 0) {
			my.renderEmptyOn(html);
			return;
		}

		html.div(
			{ klass: "coa-section clearfix" },
			my.listOfChartsOfAccountsTitle,
			my.renderChartsOfAccountsOn,
			my.renderImportChartOfAccountOn,
		);
	};

	my.renderChartsOfAccountsOn = function (html) {
		html.div(
			{
				klass: "coa-list list-stripped",
			},
			html.ul(my.chartsOfAccounts.map(my.itemRenderer)),
		);
	};

	my.renderLoadingOn = function (html) {
		html.render(my.spinnerWidget);
	};

	my.renderEmptyOn = function (html) {
		html.div(_("No chart of accounts found."));
	};

	my.itemRenderer = function (chartOfAccounts) {
		return function (html) {
			html.li(
				{
					klass: "coa-item",
					"data-test": "coa-item",
				},
				my.chartOfAccountsNameRenderer(chartOfAccounts),
				html.span(
					{
						klass: "coa-actions pull-right",
					},
					(html) => {
						if (!chartOfAccounts.isUserDefined()) {
							return;
						}

						html.div(
							{
								klass: "coa-action coa-export",
							},
							html.a(_("Export")).click(() => {
								my.export(chartOfAccounts);
							}),
						);
					},
					(html) => {
						if (
							!my.accountMappingEnabled ||
							!chartOfAccounts.isAccountMappingEnabled()
						) {
							return;
						}

						html.div(
							{
								klass: "coa-action coa-edit",
							},
							html.a(_("Update mapping")).click(() => {
								my.redirectToAccountMapping(chartOfAccounts);
							}),
						);
					},
					(html) => {
						if (!my.chartOfAccountsEditorEnabled) {
							return;
						}

						html.div(
							{
								klass: "coa-action coa-edit",
							},
							html.a(
								{
									"data-test": "edit-coa",
									click: () => my.edit(chartOfAccounts),
								},
								_("Edit"),
							),
						);
					},
					(html) => {
						if (my.canDeleteChartOfAccounts(chartOfAccounts)) {
							html.div(
								{
									klass: "coa-action coa-delete",
									"data-test": "delete-coa",
								},
								timesIcon,
							).click(() => {
								my.delete(chartOfAccounts);
							});
						} else {
							html.div({
								klass: "coa-action coa-delete",
							});
						}
					},
				),
			);
		};
	};

	my.chartOfAccountsNameRenderer = function (chartOfAccounts) {
		const name = chartOfAccounts.getName();

		if (my.isAgencyChartOfAccounts(chartOfAccounts))
			return _("{entityName} (shared)", { entityName: name });

		return name;
	};

	my.renderImportChartOfAccountOn = function (html) {
		html.render(
			FileSelectionButtonWidget.forJSONFile({
				label: _("Import a chart of accounts"),
				cssClass: "btn btn-primary pull-right",
				fileSelectedCallback: (data) => {
					my.uploadSpinner.show();
					that.onChartOfAccountsImported.trigger(data);
				},
			}),
		);
	};

	my.edit = function (chartOfAccounts) {
		that.onChartOfAccountsClicked.trigger(chartOfAccounts);
	};

	my.export = function (chartOfAccounts) {
		my.redirectToLocationPath(my.getExportUrl(chartOfAccounts));
	};

	my.redirectToAccountMapping = function (chartOfAccounts) {
		my.redirectTo(
			`account-mapping/${my.company.getLegacyId()}/editor/${chartOfAccounts.getId()}`,
		);
	};

	my.getExportUrl = function (chartOfAccounts) {
		return `/Shared/Company/${my.company.getLegacyId()}/ChartOfAccounts/Export/${chartOfAccounts.getId()}`;
	};

	my.delete = function (chartOfAccounts) {
		my.confirmDeletion({
			name: chartOfAccounts.getName(),
			callback: () => {
				my.chartsOfAccounts = my.chartsOfAccounts.filter(
					(coa) => coa.getId() !== chartOfAccounts.getId(),
				);

				that.update();
				that.onChartOfAccountsRemoved.trigger(chartOfAccounts);
			},
		});
	};
});
